import {
    computed,
    ComputedRef,
    onUnmounted,
    ref,
    watch,
    watchEffect,
} from 'vue';

import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { BaseDrawerMode, BaseDrawerSize } from '@/components/base-components/components/drawer/types';
import { APP_PERSISTENT_CLASSES, MODE, SIZE_WIDTH } from '@/components/base-components/components/drawer/constants';
import {
    getElement,
    removeCssVar,
    setCssVar,
    toggleClasses,
} from '@/components/base-components/utils/dom';

// @ts-expect-error not typed yet
import { useStack } from '@/components/base-components/composables/stack.js';

type PersistentDrawer = {
    id: number;
    size: BaseDrawerSize;
    close: () => void;
};

type DrawerRegistry = {
    zIndex: ComputedRef<number>;
    show: ComputedRef<boolean>;
    isPersistentActive: ComputedRef<boolean>;
};

const persistentInstances = ref<number[]>([]);

function addPersistentInstance(id: number) {
    if (persistentInstances.value.includes(id)) return;

    persistentInstances.value.push(id);
}

function removePersistentInstance(id: number) {
    persistentInstances.value = persistentInstances.value.filter(instanceId => instanceId !== id);
}

const activePersistentDrawer = ref<PersistentDrawer | null>(null);
const activePersistentId = computed(() => activePersistentDrawer.value?.id ?? null);

function setActivePersistentDrawer(drawer: PersistentDrawer | null) {
    activePersistentDrawer.value = drawer;
}

watchEffect(() => {
    const drawer = activePersistentDrawer.value;

    if (!drawer) {
        removeCssVar('--base-drawer-persistent-shift');
        return;
    }

    setCssVar('--base-drawer-persistent-shift', SIZE_WIDTH[drawer.size]);
});

watchEffect(() => {
    toggleClasses(getElement('#app'), APP_PERSISTENT_CLASSES, persistentInstances.value.length > 0);
});

export function baseDrawerRegistry(
    open: MaybeRefOrGetter<boolean>,
    present: MaybeRefOrGetter<boolean>,
    mode: MaybeRefOrGetter<BaseDrawerMode>,
    size: MaybeRefOrGetter<BaseDrawerSize>,
    close: () => void,
): DrawerRegistry {
    const { id, zIndex } = useStack(present);

    const isPersistent = computed(() => toValue(mode) === MODE.PERSISTENT);
    const isPersistentActive = computed(() => isPersistent.value && activePersistentId.value === id);

    const effectiveShow = computed(() => {
        if (!toValue(open)) return false;
        if (!isPersistent.value) return true;

        return isPersistentActive.value;
    });

    watch(() => toValue(mode), (mode) => {
        if (mode === MODE.PERSISTENT) {
            addPersistentInstance(id);
            return;
        }

        removePersistentInstance(id);

        if (activePersistentId.value === id) {
            setActivePersistentDrawer(null);
        }
    }, { immediate: true });

    watch(() => ({
        open: Boolean(toValue(open)),
        mode: toValue(mode),
        size: toValue(size),
    }), ({ open, mode, size }) => {
        if (mode !== MODE.PERSISTENT) return;

        if (!open) {
            if (activePersistentId.value === id) {
                setActivePersistentDrawer(null);
            }

            return;
        }

        const previousDrawer = activePersistentDrawer.value;

        setActivePersistentDrawer({ id, size, close });

        if (previousDrawer && previousDrawer.id !== id) {
            previousDrawer.close();
        }
    }, { immediate: true });

    onUnmounted(() => {
        removePersistentInstance(id);

        if (activePersistentId.value === id) {
            setActivePersistentDrawer(null);
        }
    });

    return {
        zIndex,
        show: effectiveShow,
        isPersistentActive,
    };
}
